import { db, get, off, onValue, ref, set, update } from "./firebaseApi";
import { logUserActivity } from "./activityService";
import { userApiService } from "./userApiService";

const DEVICE_POLL_MS = 30000;
const LIVE_POLL_MS = 15000;

function itemsToDeviceMap(items = []) {
  const map = {};
  items.forEach((d) => {
    map[d.deviceId] = {
      name: d.name || d.deviceId,
      createdAt: d.createdAt || 0,
      isSimulator: !!d.isSimulator,
      deviceType: d.deviceType || "",
      hubMode: !!d.hubMode,
      pumpConnected: d.pumpConnected,
      hubId: d.hubId || "",
      linkedNodes: Array.isArray(d.linkedNodes) ? d.linkedNodes : [],
    };
  });
  return map;
}

export function parseWifiScanNetworks(wifiScan = {}) {
  const networks = wifiScan?.networks;
  if (!networks) return [];
  const list = Array.isArray(networks) ? networks : Object.values(networks);
  return list
    .filter((n) => n && String(n.ssid || "").length > 0)
    .sort((a, b) => (Number(b.rssi) || -999) - (Number(a.rssi) || -999));
}

// Device heartbeats every 30s; allow two missed beats before declaring offline.
const ONLINE_WINDOW_MS = 90 * 1000;

function parseLastUpdatedMs(raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  if (typeof raw === "number") {
    return raw < 1e12 ? raw * 1000 : raw;
  }
  const str = String(raw);
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})_(\d{2})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, y, mo, d, h, mi, s] = match.map(Number);
    return new Date(y, mo - 1, d, h, mi, s).getTime();
  }
  const n = Number(str);
  if (Number.isFinite(n) && n > 1e12) return n;
  return null;
}

export function normalizeLastSeenMs(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  // Unix seconds (typical: 1.7e9+). Reject millis()/uptime garbage (< 1e9).
  if (n < 1e9) return null;
  if (n < 1e12) return n * 1000;
  if (n < 1e13) return n;
  return null;
}

export function parseDeviceLastSeen(live = {}) {
  const fromMeta = normalizeLastSeenMs(live?.meta?.lastSeen);
  const fromUpdated = parseLastUpdatedMs(live?.lastUpdated);
  // Firmware heartbeats write here even when Render /ingest is slow or failing.
  const fromStatus = normalizeLastSeenMs(live?.deviceStatus?.lastSeen);
  const candidates = [fromMeta, fromUpdated, fromStatus].filter(Boolean);
  if (!candidates.length) return null;
  return Math.max(...candidates);
}

export function isDeviceOnline(lastSeenMs, nowMs = Date.now()) {
  if (!lastSeenMs) return false;
  return nowMs - lastSeenMs <= ONLINE_WINDOW_MS;
}

export function resolveDeviceOnline(live = {}, isSimulator = false, nowMs = Date.now()) {
  if (isSimulator) return false;
  const lastSeen = parseDeviceLastSeen(live);
  return isDeviceOnline(lastSeen, nowMs);
}

function mergeLivePayload(prev = {}, next = {}) {
  const mergedMeta = { ...(prev.meta || {}), ...(next.meta || {}) };
  delete mergedMeta.online;
  const merged = {
    ...prev,
    ...next,
    meta: mergedMeta,
    deviceStatus: { ...(prev.deviceStatus || {}), ...(next.deviceStatus || {}) },
    wifiScan: next.wifiScan ?? prev.wifiScan,
    pots: {
      ...(prev.pots && typeof prev.pots === "object" ? prev.pots : {}),
      ...(next.pots && typeof next.pots === "object" ? next.pots : {}),
    },
  };
  if (!Object.keys(merged.pots).length) delete merged.pots;
  const bestSeen = parseDeviceLastSeen(merged);
  if (bestSeen) {
    merged.meta = { ...merged.meta, lastSeen: bestSeen };
  }
  return merged;
}

export function subscribeUserDevices(uid, onData, onError) {
  let cancelled = false;

  const poll = async () => {
    try {
      const data = await userApiService.listDevices();
      if (!cancelled) onData(itemsToDeviceMap(data.items || []));
    } catch (error) {
      if (!cancelled) onError?.(error);
    }
  };

  poll();
  const interval = setInterval(poll, DEVICE_POLL_MS);
  return () => {
    cancelled = true;
    clearInterval(interval);
  };
}

export async function refreshUserDevicesMap() {
  const data = await userApiService.listDevices();
  return itemsToDeviceMap(data.items || []);
}

export function subscribeDeviceLive(deviceId, onData, onError) {
  let cancelled = false;
  let merged = {};
  const liveRef = ref(db, `SmartFarming/${deviceId}`);
  const statusRef = ref(db, `DeviceStatus/${deviceId}`);
  const scanRef = ref(db, `DeviceWiFiScan/${deviceId}`);

  const emit = (data) => {
    if (cancelled || !data) return;
    merged = mergeLivePayload(merged, data);
    onData(merged);
  };

  onValue(
    liveRef,
    (snapshot) => {
      const val = snapshot.val();
      if (val) emit(val);
    },
    () => {}
  );

  onValue(
    statusRef,
    (snapshot) => {
      const val = snapshot.val();
      if (val) emit({ deviceStatus: val });
    },
    () => {}
  );

  onValue(
    scanRef,
    (snapshot) => {
      const val = snapshot.val();
      if (val) emit({ wifiScan: val });
    },
    () => {}
  );

  const poll = async () => {
    try {
      const data = await userApiService.getDeviceLive(deviceId);
      if (!cancelled && data?.item) {
        const item = data.item || {};
        emit({
          ...item,
          deviceStatus: {
            lastSeen: item?.meta?.lastSeen,
            online: item?.meta?.online,
            firmwareVersion: item?.meta?.firmwareVersion,
            rssi: item?.meta?.rssi,
            wifiSSID: item?.meta?.wifiSSID,
          },
        });
      }
    } catch {
      // API may be unavailable; Firebase stream still updates merged state.
    }
  };

  poll();
  const interval = setInterval(poll, LIVE_POLL_MS);
  return () => {
    cancelled = true;
    clearInterval(interval);
    off(liveRef);
    off(statusRef);
    off(scanRef);
  };
}

export async function requestDeviceWifiScan(deviceId) {
  const requestedAt = Math.floor(Date.now() / 1000);
  try {
    await update(ref(db, `DeviceConfig/${deviceId}`), { requestScan: requestedAt });
  } catch {
    // API path still requests the scan if RTDB write is denied.
  }
  try {
    return await userApiService.requestWifiScan(deviceId);
  } catch (error) {
    return { ok: true, requestedAt };
  }
}

export async function sendDeviceCommand(deviceId, { target = "pump", action, durationSec, sensorAtStart, potIndex, nodeId } = {}) {
  const data = await userApiService.sendDeviceCommand(deviceId, {
    target,
    action,
    ...(durationSec !== undefined ? { durationSec } : {}),
    ...(sensorAtStart ? { sensorAtStart } : {}),
    ...(potIndex !== undefined && potIndex !== null ? { potIndex } : {}),
    ...(nodeId ? { nodeId } : {}),
  });
  return {
    commandId: data.commandId,
    status: data.status || "pending",
    potIndex: data.potIndex || 0,
    nodeId: data.nodeId || "",
    queued: !!data.queued,
    queuePosition: Number(data.queuePosition || 0) || 0,
    message: data.message || "",
  };
}

export async function listDeviceCommands(deviceId, params = {}) {
  return userApiService.listDeviceCommands(deviceId, params);
}

export async function addDeviceForUser(uid, deviceId, name) {
  const normalizedId = String(deviceId || "").trim();
  const normalizedName = String(name || "").trim();

  try {
    await userApiService.addDevice({ deviceId: normalizedId, name: normalizedName });
  } catch (error) {
    if (error.code === "already_owned") {
      return { ok: false, code: "already_owned" };
    }
    if (error.code === "owned_by_other") {
      return { ok: false, code: "owned_by_other" };
    }
    if (error.code === "not_provisioned") {
      return { ok: false, code: "not_provisioned" };
    }
    if (error.code === "device_disabled") {
      return { ok: false, code: "device_disabled" };
    }
    throw error;
  }

  await logUserActivity(uid, {
    type: "device_add",
    title: `Added device '${normalizedName || normalizedId}'`,
    details: `Linked device ${normalizedId} to your account.`,
  });

  return { ok: true };
}

export async function renameUserDevice(uid, deviceId, name) {
  const nextName = String(name || "").trim();
  await userApiService.updateDevice(deviceId, { name: nextName });
  await logUserActivity(uid, {
    type: "device_rename",
    title: `Renamed device '${nextName || deviceId}'`,
    details: `Updated display name for ${deviceId}.`,
  });
}

export async function unlinkUserDevice(uid, deviceId) {
  await userApiService.deleteDevice(deviceId);
  await logUserActivity(uid, {
    type: "device_unlink",
    title: `Removed device '${deviceId}'`,
    details: "Unlinked device from your account.",
  });
}

export async function saveDeviceWifi(deviceId, ssid, password, uid = "") {
  await userApiService.updateDevice(deviceId, {
    ssid: String(ssid || "").trim(),
    password: String(password || "").trim(),
  });
  try {
    await userApiService.pushDeviceConfig(deviceId);
  } catch (error) {
    if (error?.status !== 404) throw error;
  }
  if (uid) {
    await logUserActivity(uid, {
      type: "wifi_update",
      title: `Updated Wi-Fi for '${deviceId}'`,
      details: `Pushed Wi-Fi credentials for ${ssid}.`,
    });
  }
}

export async function switchDeviceWifi(deviceId, ssid, password, uid = "") {
  return saveDeviceWifi(deviceId, ssid, password, uid);
}

function parseStoredWifiNetworks(raw) {
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : Object.values(raw);
  return list.filter((entry) => entry && String(entry.ssid || "").trim());
}

async function writeWifiNetworksToFirebase(deviceId, networks, extra = {}) {
  const networksPayload = {};
  networks.forEach((entry, index) => {
    networksPayload[index] = {
      ssid: String(entry.ssid || "").trim(),
      password: String(entry.password || ""),
      savedAt: Number(entry.savedAt || Date.now()),
    };
  });
  await set(ref(db, `DeviceConfig/${deviceId}/wifiNetworks`), networks.length ? networksPayload : null);
  await update(ref(db, `DeviceConfig/${deviceId}`), {
    wifiNetworkCount: networks.length,
    syncedAt: Date.now(),
    lastUpdatedBy: extra.lastUpdatedBy || "user",
    ...extra.fields,
  });
}

export async function removeDeviceWifi(deviceId, ssid, uid = "") {
  const network = String(ssid || "").trim();
  if (!network) return;

  let remaining = [];
  let loadedFromStore = false;
  try {
    const snap = await get(ref(db, `DeviceConfig/${deviceId}`));
    const val = snap.val() || {};
    const stored = parseStoredWifiNetworks(val.wifiNetworks);
    if (stored.length) {
      remaining = stored.filter((entry) => entry.ssid !== network);
      loadedFromStore = true;
    }
  } catch {
    remaining = [];
  }

  if (!loadedFromStore) {
    const data = await userApiService.getDeviceConfig(deviceId);
    const item = data.item || {};
    remaining = parseStoredWifiNetworks(item.wifiNetworks).filter((entry) => entry.ssid !== network);
  }

  await writeWifiNetworksToFirebase(deviceId, remaining, {
    lastUpdatedBy: "user_remove",
  });

  try {
    await userApiService.updateDevice(deviceId, { removeSsid: network });
  } catch {
    // Live API may not have removeSsid yet; Firebase list is the source of truth.
  }

  if (uid) {
    await logUserActivity(uid, {
      type: "wifi_remove",
      title: `Removed Wi-Fi '${network}'`,
      details: `Forgot saved credentials for ${network} on ${deviceId}.`,
    });
  }
}

export function subscribeDeviceConfig(deviceId, onData, onError) {
  let cancelled = false;
  const configRef = ref(db, `DeviceConfig/${deviceId}`);

  const emit = (item) => {
    if (!cancelled) onData(item || {});
  };

  onValue(
    configRef,
    (snapshot) => {
      const val = snapshot.val();
      if (!val) return;
      emit({
        ssid: val.wifiSSID || "",
        password: val.wifiPassword || "",
        wifiNetworks: parseStoredWifiNetworks(val.wifiNetworks),
        updatedAt: Number(val.syncedAt || val.configVersion || 0),
      });
    },
    (error) => {
      if (!cancelled) onError?.(error);
    }
  );

  const poll = async () => {
    try {
      const data = await userApiService.getDeviceConfig(deviceId);
      if (cancelled) return;
      const item = data.item || {};
      const snap = await get(configRef);
      const val = snap.val();
      if (val) {
        emit({
          ssid: val.wifiSSID || item.ssid || "",
          password: val.wifiPassword || item.password || "",
          wifiNetworks: parseStoredWifiNetworks(val.wifiNetworks),
          updatedAt: Number(val.syncedAt || item.updatedAt || 0),
        });
        return;
      }
      emit(item);
    } catch (error) {
      if (!cancelled) onError?.(error);
    }
  };

  poll();
  const interval = setInterval(poll, DEVICE_POLL_MS);
  return () => {
    cancelled = true;
    clearInterval(interval);
    off(configRef);
  };
}
